import { useEffect, useRef, useState } from 'react'
import type { ReactElement } from 'react'
import { Link } from 'react-router'
import { useT } from '../../i18n/useI18n'
import { resendVerification, verifyEmail } from '../../account/accountApi'
import { useLinkToken } from '../../account/useLinkToken'
import AccountLayout from './AccountLayout'
import { AccountError, AccountNotice, EmailRequestForm } from './AccountParts'

type VerifyState = { status: 'missingToken' } | { status: 'verifying' } | { status: 'verified' } | { status: 'failed'; error: Error }

const PRODUCTS_PATH: string = '/'

function ResendLink({ lead }: { lead: string }): ReactElement {
  const t = useT()
  return (
    <>
      <p className="ac-small">{lead}</p>
      <EmailRequestForm submitLabel={t('account.verify.resendSubmit')} send={resendVerification} renderDone={() => <p className="ac-small ac-strong">{t('account.register.resent')}</p>} />
    </>
  )
}

export default function AccountVerifyEmail(): ReactElement {
  const t = useT()
  const token: string | null = useLinkToken()
  const [state, setState] = useState<VerifyState>(token === null ? { status: 'missingToken' } : { status: 'verifying' })
  // The link token is single-use, so StrictMode's second effect run must not spend it again.
  const started = useRef<boolean>(false)

  useEffect(() => {
    if (token === null || started.current) return
    started.current = true
    verifyEmail(token).then(() => setState({ status: 'verified' })).catch((error: unknown) => setState({ status: 'failed', error: error as Error }))
  }, [token])

  return (
    <AccountLayout title={t('account.verify.title')}>
      {state.status === 'verifying' && <p className="ac-working"><span className="spinner-border spinner-border-sm" aria-hidden="true" /> {t('account.verify.verifying')}</p>}
      {state.status === 'verified' && (
        <>
          <AccountNotice title={t('account.verify.doneTitle')}><p>{t('account.verify.doneBody')}</p></AccountNotice>
          <p className="ac-links"><Link to={PRODUCTS_PATH}>{t('account.verify.toProducts')}</Link></p>
        </>
      )}
      {state.status === 'failed' && <><AccountError error={state.error} /><ResendLink lead={t('account.verify.failedLead')} /></>}
      {state.status === 'missingToken' && <ResendLink lead={t('account.verify.missingToken')} />}
    </AccountLayout>
  )
}
